import { CRITICAL_ASSETS } from '../../constants/critical-assets.ts';
import { LessonKey } from '../../types/asset/lesson.types.ts';
import { FontPreloadableAsset, PreloadableAsset, PreloadableAssetType } from '../../types/asset/preloading.types.ts';

import { getAudioSfxProviderUtil } from '../audio/audio-sfx-provider.util.ts';

import { getCharactersUtil } from './characters.util.ts';
import { getFontFaceUtil } from './font-face.util.ts';
import { getLessonsUtil } from './lessons.util.ts';
import { getLettersUtil } from './letters.util.ts';

export class AssetPreloadingUtil {
  private static _instance: AssetPreloadingUtil;

  private constructor(
    private readonly charactersUtil = getCharactersUtil(),
    private readonly fontFaceUtil = getFontFaceUtil(),
    private readonly audioSfxProviderUtil = getAudioSfxProviderUtil(),
    private readonly lessonsUtil = getLessonsUtil(),
    private readonly lettersUtil = getLettersUtil()
  ) {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): AssetPreloadingUtil {
    if (!AssetPreloadingUtil._instance) {
      AssetPreloadingUtil._instance = new AssetPreloadingUtil();
    }
    return AssetPreloadingUtil._instance;
  }

  public async preloadImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = (): void => resolve();
      img.onerror = (error: unknown): void => reject(new Error(String(error)));
      img.src = url;
    });
  }

  public async preloadFont(asset: FontPreloadableAsset): Promise<void> {
    const fontFace = new FontFace(asset.fontFamily, `url(${asset.url}) format("${asset.fontFormat}")`, {
      weight: asset.fontWeight.toString(),
      style: asset.fontStyle,
    });

    const loadedFont = await fontFace.load();
    document.fonts.add(loadedFont);
  }

  public async preloadAudio(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(url);
      audio.oncanplaythrough = (): void => resolve();
      audio.onerror = (error: unknown): void => reject(new Error(String(error)));
    });
  }

  public async preloadAsset(asset: PreloadableAsset): Promise<void> {
    switch (asset.type) {
      case PreloadableAssetType.Image:
        return this.preloadImage(asset.url);
      case PreloadableAssetType.Font:
        return this.preloadFont(asset);
      case PreloadableAssetType.Audio:
        return this.preloadAudio(asset.url);
    }
  }

  public async preloadCriticalAssets(): Promise<void> {
    const timeKey = 'Preloading critical assets';
    console.time(timeKey);
    const assets = await this.determineCriticalAssets();
    await Promise.all(assets.map((asset) => this.preloadAsset(asset)));
    console.timeEnd(timeKey);
  }

  public async preloadLessonAssets(lessonKey: LessonKey): Promise<void> {
    const timeKey = `Preloading lesson ${lessonKey.lesson} assets`;
    console.time(timeKey);
    const { criticalAssets, lazyAssets } = this.lessonsUtil.getLessonAssets(lessonKey);
    await this.preloadAssets(criticalAssets);
    console.timeLog(timeKey, 'Preloaded critical assets');
    this.preloadAssets(lazyAssets).then(() => {
      console.timeEnd(timeKey);
    });
  }

  public preloadAssetsInBackground(): void {
    const timeKey = 'Preloading background assets';
    console.time(timeKey);
    this.determineBackgroundAssets().then((assets) => {
      this.preloadAssets(assets).then(() => {
        console.timeEnd(timeKey);
      });
    });
  }

  private async preloadAssets(assets: PreloadableAsset[]): Promise<void> {
    await Promise.allSettled(assets.map((asset) => this.preloadAsset(asset)));
  }

  private async determineCriticalAssets(): Promise<PreloadableAsset[]> {
    return [
      ...CRITICAL_ASSETS,
      ...this.charactersUtil.getCriticalAssets(),
      ...(await this.audioSfxProviderUtil.getCriticalAssets()),
      ...this.fontFaceUtil.getFonts(),
    ];
  }

  private async determineBackgroundAssets(): Promise<PreloadableAsset[]> {
    return [
      ...this.charactersUtil.getAssets(),
      ...this.lettersUtil.getLetterAssets(),
      ...(await this.audioSfxProviderUtil.getAssets()),
    ];
  }
}

export const getAssetPreloadingUtil = (): AssetPreloadingUtil => AssetPreloadingUtil.instance;
